// src/pages/Collection.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getCollections, CollectionDto } from "../api/collectionApi";
import {
  getProductClientById,
  getClientProductList,
  ProductDetailDto,
  ProductListDto,
  FacetDto,
} from "../api/productApi";

import { useCart } from "../contexts/CartProvider";
import SafeImg from "../components/common/SafeImg";
import { gaViewItem, gaAddToCart } from "../analytics/ga";
/* ======================
   HELPERS
====================== */
const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");
const FALLBACK_IMG = "/images/placeholder.jpg";

function resolveImg(url?: string | null) {
  if (!url) return FALLBACK_IMG;
  const u = String(url).split("\\").join("/").trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return API_URL ? `${API_URL}${u}` : u;
  return API_URL ? `${API_URL}/${u}` : u;
}

function isDesktopHover() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches
  );
}

function formatVND(v: number) {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(v ?? 0);
}

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** normalize collection: chịu id/collectionId + slug/name */
function normalizeCollection(c: any): CollectionDto {
  return {
    ...c,
    id: Number(c?.id ?? c?.collectionId ?? 0),
    slug: String(c?.slug ?? ""),
    name: String(c?.name ?? ""),
  } as CollectionDto;
}

/** normalize product list từ API client/list (ProductDto) */
function normalizeProduct(p: any): ProductListDto {
  const id = Number(p?.id ?? p?.productId ?? p?.ProductId ?? 0);

  const basePrice = Number(p?.price ?? p?.Price ?? p?.basePrice ?? 0);
  const salePrice =
    p?.finalPrice != null
      ? Number(p.finalPrice)
      : p?.FinalPrice != null
        ? Number(p.FinalPrice)
        : p?.salePrice != null
          ? Number(p.salePrice)
          : null;

  const imageUrls: any[] =
    (Array.isArray(p?.imageUrls) ? p.imageUrls : null) ??
    (Array.isArray(p?.ImageUrls) ? p.ImageUrls : null) ??
    [];

  const thumbnail =
    p?.thumbnail ??
    p?.thumbnailUrl ??
    p?.imageUrl ??
    (imageUrls.length ? imageUrls[0] : null) ??
    (Array.isArray(p?.images) ? p.images?.[0]?.imageUrl : null) ??
    null;

  const name = String(p?.name ?? p?.Name ?? "");
  const sku = String(p?.sku ?? p?.SKU ?? p?.Sku ?? "").trim();
  const slug = String(p?.slug ?? (sku.length ? sku : String(id)));

  return {
    id,
    name,
    slug,
    basePrice,
    salePrice,
    thumbnail,
  };
}

/* ======================
   PAGE
====================== */
export default function Collection() {
  const navigate = useNavigate();
  const desktop = isDesktopHover();
  const { addItem, open: openCart } = useCart();

  /* ===== COLLECTIONS (UI tabs) ===== */
  const [collections, setCollections] = useState<CollectionDto[]>([]);
  const [activeCollectionSlug, setActiveCollectionSlug] = useState<
    string | null
  >(null);

  /* ===== API FILTERS (client/list) ===== */
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 350);

  const [category, setCategory] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [size, setSize] = useState<string | null>(null);
  const [inStockOnly, setInStockOnly] = useState(false);

  const [sort, setSort] = useState<"new" | "old" | "priceAsc" | "priceDesc">(
    "new",
  );

  const [dense, setDense] = useState(false);

  /* ===== DATA ===== */
  const [items, setItems] = useState<ProductListDto[]>([]);

  // ✅ facets “ổn định” (luôn đủ option), không bị mất khi bạn chọn 1 filter
  const [facets, setFacets] = useState<FacetDto>({
    colors: [],
    sizes: [],
    categories: [],
  });

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ===== PAGING ===== */
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  /* ===== QUICK VIEW ===== */
  const [openQuickId, setOpenQuickId] = useState<number | null>(null);
  const [detailCache, setDetailCache] = useState<
    Record<number, ProductDetailDto>
  >({});
  const [detailLoading, setDetailLoading] = useState<Record<number, boolean>>(
    {},
  );

  const [qColor, setQColor] = useState<string | null>(null);
  const [qSize, setQSize] = useState<string | null>(null);
  const [qQty, setQQty] = useState(1);
  const [addingQuick, setAddingQuick] = useState(false);

  /* ======================
     LOAD COLLECTIONS (chỉ để UI chips)
  ====================== */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await getCollections({
          page: 1,
          pageSize: 100,
          sort: "order",
        } as any);
        if (!alive) return;

        const list = Array.isArray(res) ? res.map(normalizeCollection) : [];
        setCollections(list);

        if (list.length > 0) {
          setActiveCollectionSlug(list[0].slug);
        }
      } catch (e: any) {
        if (!alive) return;
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load collections";
        setError(String(msg));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const collectionMap = useMemo(() => {
    const m: Record<string, string> = {};
    collections.forEach((c) => (m[c.slug] = c.name));
    return m;
  }, [collections]);

  const activeDropName = activeCollectionSlug
    ? collectionMap[activeCollectionSlug] || "Drop"
    : "Drop";

  /* ======================
     FETCH PRODUCTS: /api/Products/client/list
  ====================== */
  const reqIdRef = useRef(0);

  // ✅ helper: merge selected value vào facets để không bị mất option hiển thị
  const mergeFacet = (list: string[], selected: string | null) => {
    const arr = Array.isArray(list) ? [...list] : [];
    if (selected && !arr.includes(selected)) arr.unshift(selected);
    return arr;
  };

  const fetchList = async (p: number, append: boolean) => {
    const myId = ++reqIdRef.current;

    const qv = debouncedQ.trim() || undefined;
    const cat = category || undefined;
    const col = color || undefined;
    const sz = size || undefined;

    // 1) list theo filter đầy đủ
    const listRes = await getClientProductList({
      page: p,
      pageSize: PAGE_SIZE,
      q: qv,
      category: cat,
      color: col,
      size: sz,
      sort,
      inStockOnly,
    });

    // 2) facets theo filter "nới lỏng" (KHÔNG kẹp color/size)
    //    -> dropdown luôn đủ option
    const facetRes = await getClientProductList({
      page: 1,
      pageSize: 1,
      q: qv,
      category: cat,
      // color: undefined,
      // size: undefined,
      sort,
      inStockOnly,
    });

    if (myId !== reqIdRef.current) return;

    const data = (listRes.items || []).map(normalizeProduct);

    setTotalPages(Number(listRes.pagination?.totalPages ?? 1) || 1);

    const nextFacets = facetRes.facets ?? {
      colors: [],
      sizes: [],
      categories: [],
    };
    setFacets({
      categories: mergeFacet(nextFacets.categories ?? [], category),
      colors: mergeFacet(nextFacets.colors ?? [], color),
      sizes: mergeFacet(nextFacets.sizes ?? [], size),
    });

    setItems((prev) => (append ? [...prev, ...data] : data));
  };

  // reset page khi filter/sort đổi
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);

    fetchList(1, false)
      .catch((e: any) => {
        const msg =
          e?.response?.data?.message || e?.message || "Failed to load products";
        if (!cancelled) setError(String(msg));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, category, color, size, sort, inStockOnly]);

  /* ======================
     INFINITE SCROLL
  ====================== */
  const pageRef = useRef(page);
  const totalRef = useRef(totalPages);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);

  useEffect(() => {
    pageRef.current = page;
    totalRef.current = totalPages;
    loadingRef.current = loading;
    loadingMoreRef.current = loadingMore;
  }, [page, totalPages, loading, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loadingRef.current || loadingMoreRef.current) return;
        if (pageRef.current >= totalRef.current) return;

        const next = pageRef.current + 1;
        setLoadingMore(true);
        try {
          await fetchList(next, true);
          setPage(next);
        } finally {
          setLoadingMore(false);
        }
      },
      { rootMargin: "800px 0px" },
    );

    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelRef]);

  /* ======================
     NAV
  ====================== */
  const go = (to: string) => {
    const doc = document as any;
    if (doc.startViewTransition) doc.startViewTransition(() => navigate(to));
    else navigate(to);
  };

  /* ======================
     QUICK VIEW
  ====================== */
  const openQuickFor = async (productId: number) => {
    if (!productId) return;

    setOpenQuickId(productId);
    setQColor(null);
    setQSize(null);
    setQQty(1);

    if (detailCache[productId] || detailLoading[productId]) return;

    setDetailLoading((m) => ({ ...m, [productId]: true }));
    try {
      const d = await getProductClientById(productId);
      setDetailCache((m) => ({ ...m, [productId]: d }));

      // ✅ GA4 view_item (Quick view cũng coi như user view item)
      const price = Number(d.salePrice ?? d.basePrice ?? 0);

      gaViewItem({
        currency: "VND",
        value: price,
        item: {
          item_id: String(d.id ?? productId),
          item_name: String(d.name ?? ""),
          price,
          item_brand: "OWL DREAM",
          item_category: "Product",
          // chưa chọn variant trong quick view lúc này
        },
      });
    } finally {
      setDetailLoading((m) => ({ ...m, [productId]: false }));
    }
  };

  const closeQuick = () => setOpenQuickId(null);

  const quickDetail =
    openQuickId != null ? detailCache[openQuickId] : undefined;
  const quickLoading = openQuickId != null ? detailLoading[openQuickId] : false;

  const quickColors = useMemo(() => {
    if (!quickDetail) return [];
    return Array.from(
      new Set(
        quickDetail.variants
          .filter((v) => v.stockQuantity > 0)
          .map((v) => v.color),
      ),
    );
  }, [quickDetail]);

  const quickSizes = useMemo(() => {
    if (!quickDetail || !qColor) return [];
    return Array.from(
      new Set(
        quickDetail.variants
          .filter((v) => v.stockQuantity > 0 && v.color === qColor)
          .map((v) => v.size),
      ),
    );
  }, [quickDetail, qColor]);

  const quickVariant = useMemo(() => {
    if (!quickDetail || !qColor || !qSize) return undefined;
    return quickDetail.variants.find(
      (v) => v.color === qColor && v.size === qSize,
    );
  }, [quickDetail, qColor, qSize]);

  const quickPrice = useMemo(() => {
    if (!quickDetail) return 0;
    return quickDetail.salePrice ?? quickDetail.basePrice;
  }, [quickDetail]);

  const quickStock = quickVariant?.stockQuantity;

  const handleQuickAdd = async () => {
    if (!quickVariant || !openQuickId || !qColor || !qSize || !quickDetail) return;

    try {
      setAddingQuick(true);

      await addItem({
        productId: openQuickId,
        quantity: qQty,
        color: qColor,
        size: qSize,
      });

      // ✅ GA4 add_to_cart (sau khi addItem thành công)
      const price = Number(quickDetail.salePrice ?? quickDetail.basePrice ?? 0);

      gaAddToCart({
        currency: "VND",
        value: price * qQty,
        quantity: qQty,
        item: {
          item_id: String(quickDetail.id ?? openQuickId),
          item_name: String(quickDetail.name ?? ""),
          price,
          item_brand: "OWL DREAM",
          item_category: "Product",
          item_variant: `${qColor}-${qSize}`,
        },
      });

      openCart();
      closeQuick();
    } finally {
      setAddingQuick(false);
    }
  };

  const SkeletonCard = () => (
    <div className="rounded-3xl border border-brand-brown/12 overflow-hidden bg-brand-ivory/80">
      <div className="aspect-[4/5] bg-brand-brown/5 animate-pulse" />
      <div className="p-5">
        <div className="h-3 w-24 bg-brand-brown/10 animate-pulse" />
        <div className="mt-4 h-4 w-2/3 bg-brand-brown/10 animate-pulse" />
        <div className="mt-3 h-4 w-28 bg-brand-brown/10 animate-pulse" />
      </div>
    </div>
  );

  /* ======================
     UI
  ====================== */
  return (
    <div className="bg-brand-ivory min-h-screen text-brand-brown">
      {/* HERO */}
      <section className="pt-24 pb-12 sm:pb-14 border-b border-brand-brown/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-end">
            <div>
              <p className="text-[11px] uppercase tracking-[0.6em] opacity-60">
                OWL DREAM — Collection
              </p>

              <h1 className="mt-6 text-3xl sm:text-4xl md:text-5xl uppercase tracking-[0.28em] leading-[1.1]">
                Quiet Luxury <span className="opacity-60">/</span> Street Core
              </h1>

              <p className="mt-5 text-sm sm:text-[15px] opacity-70 max-w-xl leading-loose">
                Minimal form. Strong silhouette. Limited drops built for night
                thinkers.
              </p>

              {/* Collection chips (UI only) */}
              <div className="mt-8 flex flex-wrap gap-2">
                {collections.slice(0, 8).map((c) => {
                  const active = c.slug === activeCollectionSlug;
                  return (
                    <button
                      key={c.slug}
                      onClick={() => {
                        setActiveCollectionSlug(c.slug);
                        setQ("");
                      }}
                      className={cn(
                        "h-9 px-4 rounded-full border text-[11px] uppercase tracking-[0.35em] transition",
                        active
                          ? "border-brand-brown bg-brand-brown text-brand-ivory"
                          : "border-brand-brown/15 hover:border-brand-brown/40",
                      )}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex lg:justify-end gap-10 text-xs uppercase tracking-[0.4em] opacity-80">
              <Link to="/drops" className="hover:opacity-60 transition">
                Drops
              </Link>
              {/* <Link to="/lookbook" className="hover:opacity-60 transition">
                Lookbook
              </Link> */}
            </div>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <div className="sticky top-0 z-30 bg-brand-ivory/85 backdrop-blur border-b border-brand-brown/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search piece…"
                className="h-11 w-[240px] sm:w-[320px] px-5 rounded-full border border-brand-brown/15 bg-transparent text-xs tracking-[0.2em] outline-none focus:border-brand-brown/45 transition"
              />
              {q.length > 0 && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.35em] opacity-60 hover:opacity-100"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Category filter */}
            <select
              value={category ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setCategory(v ? v : null);
              }}
              className="h-11 px-5 rounded-full border border-brand-brown/15 bg-transparent text-xs uppercase tracking-[0.3em] outline-none hover:border-brand-brown/40 transition"
            >
              <option value="">All Category</option>
              {(facets.categories ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {/* Color filter */}
            <select
              value={color ?? ""}
              onChange={(e) => setColor(e.target.value ? e.target.value : null)}
              className="h-11 px-5 rounded-full border border-brand-brown/15 bg-transparent text-xs uppercase tracking-[0.3em] outline-none hover:border-brand-brown/40 transition"
            >
              <option value="">All Color</option>
              {(facets.colors ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {/* Size filter */}
            <select
              value={size ?? ""}
              onChange={(e) => setSize(e.target.value ? e.target.value : null)}
              className="h-11 px-5 rounded-full border border-brand-brown/15 bg-transparent text-xs uppercase tracking-[0.3em] outline-none hover:border-brand-brown/40 transition"
            >
              <option value="">All Size</option>
              {(facets.sizes ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {/* In stock */}
            <button
              type="button"
              onClick={() => setInStockOnly((v) => !v)}
              className={cn(
                "h-11 px-5 rounded-full border text-xs uppercase tracking-[0.3em] transition",
                inStockOnly
                  ? "border-brand-brown bg-brand-brown text-brand-ivory"
                  : "border-brand-brown/15 hover:border-brand-brown/40",
              )}
              title="In stock only"
            >
              In stock
            </button>

            {/* Clear filters */}
            <button
              type="button"
              onClick={() => {
                setCategory(null);
                setColor(null);
                setSize(null);
                setInStockOnly(false);
              }}
              className="h-11 px-5 rounded-full border border-brand-brown/15 text-xs uppercase tracking-[0.3em] hover:border-brand-brown/40 transition"
              title="Clear filters"
            >
              Reset
            </button>
          </div>

          <div className="flex items-center gap-3 justify-between sm:justify-end">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as any)}
              className="h-11 px-5 rounded-full border border-brand-brown/15 bg-transparent text-xs uppercase tracking-[0.3em] outline-none hover:border-brand-brown/40 transition"
            >
              <option value="new">New</option>
              <option value="old">Old</option>
              <option value="priceAsc">Price ↑</option>
              <option value="priceDesc">Price ↓</option>
            </select>

            <button
              type="button"
              onClick={() => setDense((v) => !v)}
              className="h-11 px-5 rounded-full border border-brand-brown/15 text-xs uppercase tracking-[0.3em] hover:border-brand-brown/40 transition"
              title="Grid density"
            >
              {dense ? "Comfort" : "Dense"}
            </button>
          </div>
        </div>
      </div>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {loading && (
          <div
            className={cn(
              "grid gap-6",
              dense
                ? "grid-cols-2 md:grid-cols-4"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
            )}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {!loading && error && (
          <p className="text-center text-sm text-red-600">{error}</p>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="text-center">
            <p className="text-sm opacity-60">No products found.</p>
            <p className="mt-2 text-xs uppercase tracking-[0.45em] opacity-50">
              {q.trim()
                ? "Try another keyword"
                : `Collection: ${activeDropName}`}
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div
            className={cn(
              "grid gap-x-5 gap-y-10 sm:gap-x-6 sm:gap-y-12 lg:gap-x-8 lg:gap-y-14",
              dense
                ? "grid-cols-2 md:grid-cols-4"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
            )}
          >
            {items.map((p) => {
              const id = Number((p as any).id ?? 0);
              const basePrice = Number((p as any).basePrice ?? 0);
              const salePrice = (p as any).salePrice;
              const price =
                typeof salePrice === "number" ? salePrice : basePrice;
              const hasSale =
                typeof salePrice === "number" &&
                salePrice > 0 &&
                salePrice < basePrice;

              return (
                <article key={id} className="group">
                  <div className="rounded-3xl border border-brand-brown/12 overflow-hidden bg-brand-ivory/80 shadow-[0_35px_120px_rgba(43,31,26,0.10)]">
                    <div className="relative">
                      <button
                        onClick={() => go(`/product/${id}`)}
                        className="block w-full"
                      >
                        <div className="aspect-[4/5] overflow-hidden bg-brand-brown/5">
                          <SafeImg
                            src={resolveImg((p as any).thumbnail)}
                            alt={(p as any).name}
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                          />
                        </div>
                      </button>

                      <div className="absolute left-3 top-3 flex gap-2">
                        <span className="px-3 py-1 rounded-full border border-brand-brown/20 bg-brand-ivory/85 text-[10px] uppercase tracking-[0.35em]">
                          {q.trim() ? "Search" : activeDropName}
                        </span>
                        {hasSale && (
                          <span className="px-3 py-1 rounded-full bg-brand-brown text-brand-ivory text-[10px] uppercase tracking-[0.35em]">
                            Sale
                          </span>
                        )}
                      </div>

                      <div className="absolute right-3 top-3">
                        <button
                          onClick={() => openQuickFor(id)}
                          className={cn(
                            "h-9 px-4 rounded-full border border-brand-brown/20 bg-brand-ivory/90 text-[10px] uppercase tracking-[0.35em] transition",
                            "opacity-100 md:opacity-0 md:group-hover:opacity-100",
                          )}
                        >
                          Quick view
                        </button>
                      </div>

                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/10 to-transparent opacity-60" />
                    </div>

                    <div className={cn("p-5", dense && "p-4")}>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-sm tracking-[0.12em] leading-snug">
                          {(p as any).name}
                        </h3>
                        <div className="text-right">
                          <div className="text-sm">{formatVND(price)}</div>
                          {hasSale && (
                            <div className="text-[11px] opacity-50 line-through">
                              {formatVND(basePrice)}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <button
                          onClick={() => go(`/product/${id}`)}
                          className="text-[10px] uppercase tracking-[0.4em] underline underline-offset-4 opacity-70 hover:opacity-100 transition"
                        >
                          View
                        </button>

                        {desktop && (
                          <button
                            onClick={() => openQuickFor(id)}
                            className="text-[10px] uppercase tracking-[0.4em] opacity-70 hover:opacity-100 transition"
                          >
                            Add fast →
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div ref={sentinelRef} />

        {loadingMore && (
          <p className="text-center mt-10 text-xs uppercase tracking-[0.45em] opacity-60">
            Loading more…
          </p>
        )}
      </section>

      {/* QUICK VIEW MODAL */}
      {openQuickId != null && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/55" onClick={closeQuick} />

          <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
            <div className="w-full md:w-[920px] bg-brand-ivory border border-brand-brown/15 rounded-t-3xl md:rounded-3xl overflow-hidden shadow-[0_60px_180px_rgba(0,0,0,0.35)]">
              <div className="p-5 sm:p-6 border-b border-brand-brown/10 flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                  Quick View
                </p>
                <button
                  onClick={closeQuick}
                  className="h-10 w-10 rounded-full border border-brand-brown/15 hover:border-brand-brown/40 transition"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="grid md:grid-cols-[1.05fr_0.95fr] gap-0">
                <div className="border-b md:border-b-0 md:border-r border-brand-brown/10 bg-brand-brown/[0.03]">
                  <div className="p-5 sm:p-6">
                    {quickLoading && (
                      <div className="aspect-[4/5] bg-brand-brown/10 animate-pulse rounded-2xl" />
                    )}

                    {!quickLoading && quickDetail && (
                      <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-brand-brown/12 bg-brand-ivory">
                        <SafeImg
                          src={resolveImg(
                            (quickDetail as any)?.thumbnail ??
                              (Array.isArray((quickDetail as any)?.images)
                                ? (quickDetail as any).images?.[0]?.imageUrl
                                : null) ??
                              null,
                          )}
                          alt={quickDetail.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-5 sm:p-6">
                  {!quickDetail ? (
                    <p className="text-sm opacity-60">Loading…</p>
                  ) : (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                        OWL DREAM
                      </p>

                      <h3 className="mt-4 text-2xl uppercase tracking-[0.22em] leading-tight">
                        {quickDetail.name}
                      </h3>

                      <div className="mt-5 flex items-end justify-between">
                        <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                          Price
                        </p>
                        <p className="text-lg">{formatVND(quickPrice)}</p>
                      </div>

                      <div className="mt-6 h-px bg-brand-brown/10" />

                      {/* Color */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                            Color
                          </p>
                          {qColor && (
                            <p className="text-[10px] uppercase tracking-[0.55em] opacity-40">
                              {qColor}
                            </p>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {quickColors.map((c) => {
                            const active = c === qColor;
                            return (
                              <button
                                key={c}
                                onClick={() => {
                                  setQColor(c);
                                  setQSize(null);
                                  setQQty(1);
                                }}
                                className={cn(
                                  "h-10 px-4 rounded-full border text-[11px] uppercase tracking-[0.35em] transition",
                                  active
                                    ? "border-brand-brown bg-brand-brown text-brand-ivory"
                                    : "border-brand-brown/15 hover:border-brand-brown/45",
                                )}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Size */}
                      <div className="mt-5">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                            Size
                          </p>
                          {qSize && (
                            <p className="text-[10px] uppercase tracking-[0.55em] opacity-40">
                              {qSize}
                            </p>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {!qColor ? (
                            <p className="text-xs opacity-60">
                              Chọn màu trước.
                            </p>
                          ) : quickSizes.length === 0 ? (
                            <p className="text-xs opacity-60">
                              Màu này hết size còn hàng.
                            </p>
                          ) : (
                            quickSizes.map((s) => {
                              const active = s === qSize;
                              return (
                                <button
                                  key={s}
                                  onClick={() => setQSize(s)}
                                  className={cn(
                                    "h-10 px-4 rounded-full border text-[11px] uppercase tracking-[0.35em] transition",
                                    active
                                      ? "border-brand-brown bg-brand-brown text-brand-ivory"
                                      : "border-brand-brown/15 hover:border-brand-brown/45",
                                  )}
                                >
                                  {s}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {/* Qty */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase tracking-[0.55em] opacity-60">
                            Quantity
                          </p>
                          {typeof quickStock === "number" && (
                            <p className="text-xs opacity-60">
                              Stock: {quickStock}
                            </p>
                          )}
                        </div>

                        <div className="mt-3 inline-flex items-center rounded-full border border-brand-brown/15 overflow-hidden">
                          <button
                            onClick={() => setQQty((x) => Math.max(1, x - 1))}
                            className="h-10 w-12 hover:bg-brand-brown/5 transition"
                          >
                            −
                          </button>
                          <div className="h-10 w-12 grid place-items-center text-sm">
                            {qQty}
                          </div>
                          <button
                            onClick={() =>
                              setQQty((x) =>
                                typeof quickStock === "number" && quickStock > 0
                                  ? Math.min(quickStock, x + 1)
                                  : x + 1,
                              )
                            }
                            className="h-10 w-12 hover:bg-brand-brown/5 transition"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="mt-7 grid grid-cols-2 gap-3">
                        <button
                          onClick={() => {
                            closeQuick();
                            go(`/product/${openQuickId}`);
                          }}
                          className="h-12 rounded-full border border-brand-brown text-xs uppercase tracking-[0.45em] hover:bg-brand-brown hover:text-brand-ivory transition"
                        >
                          Full page
                        </button>

                        <button
                          disabled={!quickVariant || addingQuick}
                          onClick={handleQuickAdd}
                          className="h-12 rounded-full bg-brand-brown text-brand-ivory text-xs uppercase tracking-[0.45em] disabled:opacity-35 hover:opacity-90 transition"
                        >
                          {addingQuick ? "Adding…" : "Add to cart"}
                        </button>
                      </div>

                      {!quickVariant && (
                        <p className="mt-3 text-xs opacity-60">
                          Chọn màu + size để add nhanh.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="h-10" />
    </div>
  );
}
